import { Controller } from '@hotwired/stimulus'

const maskPresets: Record<string, any> = {
    phone: { mask: '+{7}(000) 000-00-00' },
    date: { mask: '00.00.0000' },
    number: { mask: Number },
    inn: { mask: '000000000000', eager: false },
    bik: { mask: '000000000' },
    account: { mask: '00000000000000000000' },
}

export default class extends Controller {
    static targets = ['input', 'errorContainer', 'clear', 'password']
    static values = { 
        mask: String,
        maskConfig: Object
    }

    declare readonly inputTarget: HTMLInputElement
    declare readonly hasInputTarget: boolean
    declare readonly errorContainerTarget: HTMLElement
    declare readonly hasErrorContainerTarget: boolean
    declare readonly clearTarget: HTMLElement
    declare readonly hasClearTarget: boolean
    declare readonly passwordTarget: HTMLElement
    declare readonly hasPasswordTarget: boolean

    declare readonly maskValue: string
    declare readonly hasMaskValue: boolean
    declare readonly maskConfigValue: any
    declare readonly hasMaskConfigValue: boolean

    private maskInstance: any | null = null

    connect() {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        input.addEventListener('change', this.handleChange as EventListener)
        input.addEventListener('input:add-error', this.handleAddError as EventListener)
        input.addEventListener('input:clear-error', this.handleClearError as EventListener)
        input.addEventListener('input:set-loading', this.handleSetLoading as EventListener)
        input.addEventListener('input:set-success', this.handleSetSuccess as EventListener)

        this.initializeMask(input)
    }

    disconnect() {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        input.removeEventListener('change', this.handleChange as EventListener)
        input.removeEventListener('input:add-error', this.handleAddError as EventListener)
        input.removeEventListener('input:clear-error', this.handleClearError as EventListener)
        input.removeEventListener('input:set-loading', this.handleSetLoading as EventListener)
        input.removeEventListener('input:set-success', this.handleSetSuccess as EventListener)

        this.destroyMask()
    }

    getValue(): string {
        const input = this.resolveInput()

        if (!input) {
            return ''
        }

        if (this.maskInstance) {
            return this.maskInstance.unmaskedValue
        }

        return input.value
    }

    setValue(value: string): void {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        if (this.maskInstance) {
            this.maskInstance.unmaskedValue = value
        } else {
            input.value = value
        }
    }

    getInput(): HTMLInputElement | null {
        return this.resolveInput()
    }

    clear(event: Event) {
        event.preventDefault()
        event.stopPropagation()

        const input = this.resolveInput()

        if (!input) {
            return
        }

        if (this.maskInstance) {
            this.maskInstance.unmaskedValue = ''
        } else {
            input.value = ''
        }

        input.dispatchEvent(new Event('input', { bubbles: true }))
        input.dispatchEvent(new Event('change', { bubbles: true }))
    }

    togglePassword(event: Event): void {
        event.preventDefault()
        event.stopPropagation()

        const input = this.hasInputTarget ? this.inputTarget : (this.element.querySelector('input') as HTMLInputElement | null)
        if (!input) {
            return
        }

        input.type = input.type === 'password' ? 'text' : 'password'
    }

    setError(message: string): void {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        input.classList.remove('success', 'loading')
        input.classList.add('error')

        if (this.hasErrorContainerTarget) {
            this.errorContainerTarget.textContent = message
        }

        this.element.dispatchEvent(new CustomEvent('input:error-added'))
    }

    clearError(): void {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        input.classList.remove('error')

        if (this.hasErrorContainerTarget) {
            this.errorContainerTarget.textContent = ''
        }

        this.element.dispatchEvent(new CustomEvent('input:error-cleared'))
    }

    setLoading(loading: boolean): void {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        input.classList.toggle('loading', loading)

        if (loading) {
            input.classList.remove('error', 'success')
        }
    }

    setSuccess(success: boolean): void {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        input.classList.toggle('success', success)

        if (success) {
            input.classList.remove('error', 'loading')
        }
    }

    private handleChange = (event: Event) => {
        this.clearError()
    }

    private handleAddError = (event: CustomEvent) => {
        const message = (event.detail && (event.detail.message as string)) || ''

        if (!message) {
            return
        }

        this.setError(message)
    }

    private handleClearError = () => {
        this.clearError()
    }

    private handleSetLoading = (event: CustomEvent) => {
        const value = !!(event.detail && event.detail.loading)
        this.setLoading(value)
    }

    private handleSetSuccess = (event: CustomEvent) => {
        const value = !!(event.detail && event.detail.success)
        this.setSuccess(value)
    }

    private resolveInput(): HTMLInputElement | null {
        if (this.hasInputTarget) {
            return this.inputTarget
        }

        return this.element.querySelector('input') as HTMLInputElement | null
    }

    private initializeMask(input: HTMLInputElement) {
        if (!this.hasMaskValue && !this.hasMaskConfigValue) {
            return
        }

        const maskConfig = this.buildMaskConfig()

        if (!maskConfig) {
            return
        }

        import('imask').then((IMask) => {
            // @ts-ignore
            this.maskInstance = IMask.default(input, maskConfig)
        })
    }

    private destroyMask() {
        if (this.maskInstance) {
            this.maskInstance.destroy()
            this.maskInstance = null
        }
    }

    private buildMaskConfig(): any {
        if (this.hasMaskConfigValue) {
            const config = this.maskConfigValue as any
            return typeof config === 'object' ? { ...config } : config
        }

        if (!this.hasMaskValue) {
            return null
        }

        return maskPresets[this.maskValue] ?? null
    }
}
